/**
 * The Studio builder registry (EAINT-858) — mirrors the shape of `OrgSettings/sections.tsx` and
 * `MetasphereAdmin/sections.tsx`. `status` is the single source of truth for every behavioural
 * difference between builders: whether a home tile shows a count, whether the route renders a
 * real builder, a read-only gallery, or the scaffolded placeholder, and whether
 * `studio_builder_opened` reports `active`/`browse`/`scaffolded`. No component may branch on `id`
 * itself. `browse` (EAINT-873) sits between the two: a real, permission-honest gallery + read-only
 * drawer exists, but no editor/composer has shipped yet — home tiles show real counts and
 * `studioRouteForTemplate` resolves a live link, unlike `scaffolded`.
 */

/** Navigation-only identifier. Deliberately NOT `ComposerTargetKind` (registryGrammar.ts) — see
 * the spec's SC7 decision note: widening the placement grammar to cover builders nobody has
 * designed yet would fabricate a contract no consumer can honour. */
export type StudioBuilderId = 'approach' | 'arc' | 'aspect' | 'context' | 'space' | 'experience';

export interface StudioBuilder {
  id: StudioBuilderId;
  /** Route segment under `/studio`, e.g. `/studio/space`. */
  segment: string;
  /**
   * Extra `/studio/<segment>` route segments that belong to this builder but are not its nav
   * target. The Space Designer owns `/studio/composition/:name` as well as `/studio/space*`, so
   * without this the shell would treat a composition view as "no builder" — no nav highlight, no
   * breadcrumb leaf, no `studio_builder_opened`.
   */
  aliasSegments?: readonly string[];
  /**
   * Per-alias breadcrumb/nav-title override — `aliasSegments` alone would read as this builder's
   * own `labelKey` while an alias route is open, e.g. the Sphere Type Editor would breadcrumb
   * "Studio > Approach" though nothing on screen says Approach. Keyed by the alias segment; a
   * segment absent here still falls back to `labelKey`.
   */
  aliasLabelKeys?: Readonly<Record<string, string>>;
  labelKey: string;
  descriptionKey: string;
  /** PrimeIcon class. */
  icon: string;
  status: 'active' | 'browse' | 'scaffolded';
  /**
   * `template.type` values (schema-service's unified registry taxonomy) this builder is the
   * canonical destination for. Bridges the desk's per-type `counts`/`DeskTemplateRef.type` back
   * to a builder — `counts` is keyed by template type, this registry by builder id, and nothing
   * else can map one to the other (EAINT-865 ratified decision). Absent = no builder owns that
   * type yet (current behaviour unchanged).
   */
  templateTypes?: readonly string[];
}

// Order follows the product architecture doc §8.1 builder table.
export const STUDIO_BUILDERS: readonly StudioBuilder[] = [
  {
    id: 'approach',
    segment: 'approach',
    // The Sphere Type Editor (EAINT-892) lives at `/studio/types/:schemaId`, not under
    // `/studio/approach` — without this alias `findCurrentBuilder` (StudioShell) would see no
    // owning builder while a type is open, so the nav loses its highlight and the breadcrumb
    // leaf.
    aliasSegments: ['types'],
    aliasLabelKeys: { types: 'studio.builder.types.label' },
    labelKey: 'studio.builder.approach.label',
    descriptionKey: 'studio.builder.approach.description',
    icon: 'pi pi-sitemap',
    status: 'browse',
    // Includes 'schema' — the Approach gallery's second section (spec SC2 "Sphere Types") ships
    // schemas alongside approaches; this is the only bridge from a schema's template type back to
    // the builder that browses it (no schema-only builder exists).
    templateTypes: ['approach', 'schema'],
  },
  {
    id: 'arc',
    segment: 'arc',
    labelKey: 'studio.builder.arc.label',
    descriptionKey: 'studio.builder.arc.description',
    icon: 'pi pi-directions',
    status: 'browse',
    templateTypes: ['arc_template'],
  },
  {
    id: 'aspect',
    segment: 'aspect',
    labelKey: 'studio.builder.aspect.label',
    descriptionKey: 'studio.builder.aspect.description',
    icon: 'pi pi-th-large',
    status: 'browse',
    templateTypes: ['aspect'],
  },
  {
    // Second active builder (EAINT-862): re-parents the pre-existing `/contexts*` library +
    // editor, unchanged, under Studio. Ordered between Aspect and Space per the canonical
    // address (Sphere → Approach → Context → Space → View) — "Context owns the Atlas".
    id: 'context',
    segment: 'context',
    labelKey: 'studio.builder.context.label',
    descriptionKey: 'studio.builder.context.description',
    icon: 'pi pi-database',
    status: 'active',
    templateTypes: ['context'],
  },
  {
    id: 'space',
    segment: 'space',
    aliasSegments: ['composition'],
    labelKey: 'studio.builder.space.label',
    descriptionKey: 'studio.builder.space.description',
    icon: 'pi pi-desktop',
    status: 'active',
    templateTypes: ['metaspace', 'composition'],
  },
  {
    id: 'experience',
    segment: 'experience',
    labelKey: 'studio.builder.experience.label',
    descriptionKey: 'studio.builder.experience.description',
    icon: 'pi pi-play',
    status: 'scaffolded',
    // No `template.type` exists for experience yet (it composes approach+arc+space; not its own
    // registry row) — omitted rather than fabricated.
  },
];

export const DEFAULT_STUDIO_BUILDER_ID: StudioBuilderId = 'space';

export const DEFAULT_STUDIO_BUILDER: StudioBuilder = STUDIO_BUILDERS.find(
  (builder) => builder.id === DEFAULT_STUDIO_BUILDER_ID
) as StudioBuilder;
